import React, { useEffect, useState } from 'react';
import { Tabs } from '../components/ui/Tabs';
import { Dashboard } from '../components/app/Dashboard';
import { IncidentsTab } from '../components/app/IncidentsTab';
import { TimelineTab } from '../components/app/TimelineTab';
import { PeopleTab } from '../components/app/PeopleTab';
import { EvidenceTab } from '../components/app/EvidenceTab';
import { CheckInsTab } from '../components/app/CheckInsTab';
import { LawyerPrepTab } from '../components/app/LawyerPrepTab';
import { ProfileTab } from '../components/app/ProfileTab';
import { DisclaimerModal } from '../components/app/DisclaimerModal';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useAuth } from '../hooks/useAuth';
import { LayoutDashboard, FileText, Clock, Users, Folder, HeartPulse, Scale, UserCircle } from 'lucide-react';
interface MainAppProps {
  onNavigate: (page: string) => void;
}
export function MainApp({
  onNavigate
}: MainAppProps) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const {
    data,
    updateData,
    addItem,
    updateItem,
    isLoaded
  } = useLocalStorage();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      onNavigate('login');
    }
  }, [isAuthenticated, isLoading, onNavigate]);
  useEffect(() => {
    if (isLoaded && !data.hasAcceptedDisclaimer) {
      setShowDisclaimer(true);
    }
  }, [isLoaded, data.hasAcceptedDisclaimer]);
  if (isLoading || !isLoaded) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-770">
        Loading...
      </div>;
  }
  const tabs = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="w-4 h-4" data-id="element-771" />
  }, {
    id: 'incidents',
    label: 'Incidents',
    icon: <FileText className="w-4 h-4" data-id="element-772" />
  }, {
    id: 'timeline',
    label: 'Timeline',
    icon: <Clock className="w-4 h-4" data-id="element-773" />
  }, {
    id: 'people',
    label: 'People',
    icon: <Users className="w-4 h-4" data-id="element-774" />
  }, {
    id: 'evidence',
    label: 'Evidence',
    icon: <Folder className="w-4 h-4" data-id="element-775" />
  }, {
    id: 'checkins',
    label: 'Check-ins',
    icon: <HeartPulse className="w-4 h-4" data-id="element-776" />
  }, {
    id: 'lawyer',
    label: 'Lawyer Prep',
    icon: <Scale className="w-4 h-4" data-id="element-777" />
  }, {
    id: 'profile',
    label: 'Profile',
    icon: <UserCircle className="w-4 h-4" data-id="element-778" />
  }];
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-779">
      {/* Beta Warning Banner */}
      <div className="bg-yellow-300 px-4 py-2 text-center text-yellow-900 text-sm font-bold flex justify-center items-center gap-2" data-id="element-780">
        <span data-id="element-781">⚠️ BETA VERSION - NOT HIPAA COMPLIANT</span>
        <button onClick={() => setShowDisclaimer(true)} className="underline hover:text-yellow-950 font-normal ml-2" data-id="element-782">
          Read Disclaimer
        </button>
      </div>

      {/* Header */}
      <header className="bg-primary-gradient text-white shadow-lg" data-id="element-783">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-784">
          <div className="flex justify-between items-center" data-id="element-785">
            <div data-id="element-786">
              <img src="https://cdn.magicpatterns.com/uploads/vHmYpRZuDDMeUH1gMkBiyX/KB_Signature_(3).png" alt="Zapiis" className="h-8 mb-2" data-id="element-787" />
              <p className="text-white/80 text-sm" data-id="element-788">
                Secure Legal Evidence Management
              </p>
            </div>
            <div className="flex items-center gap-4" data-id="element-789">
              <span className="text-sm bg-white/20 px-3 py-1 rounded-full" data-id="element-790">
                {data.profile.name || 'User'}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-791" />

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-792">
        {activeTab === 'dashboard' && <Dashboard data={data} onSaveSafetyCheck={check => addItem('safetyChecks', {
        ...check,
        id: Date.now().toString()
      })} data-id="element-793" />}
        {activeTab === 'incidents' && <IncidentsTab data={data} onAddIncident={inc => addItem('incidents', {
        ...inc,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      })} data-id="element-794" />}
        {activeTab === 'timeline' && <TimelineTab data={data} data-id="element-795" />}
        {activeTab === 'people' && <PeopleTab data={data} onAddPerson={p => addItem('people', {
        ...p,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      })} data-id="element-796" />}
        {activeTab === 'evidence' && <EvidenceTab data={data} onAddEvidence={e => addItem('evidence', {
        ...e,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      })} data-id="element-797" />}
        {activeTab === 'checkins' && <CheckInsTab data={data} onAddCheckIn={c => addItem('checkins', {
        ...c,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      })} data-id="element-798" />}
        {activeTab === 'lawyer' && <LawyerPrepTab data={data} onUpdateIntake={answers => updateData({
        intakeAnswers: {
          ...data.intakeAnswers,
          ...answers
        }
      })} data-id="element-799" />}
        {activeTab === 'profile' && <ProfileTab data={data} onUpdateProfile={p => updateData({
        profile: {
          ...data.profile,
          ...p
        }
      })} data-id="element-800" />}
      </main>

      <DisclaimerModal isOpen={showDisclaimer} onAccept={() => {
      updateData({
        hasAcceptedDisclaimer: true
      });
      setShowDisclaimer(false);
    }} data-id="element-801" />
    </div>;
}